import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from 'lib/utils';
import * as React from 'react';

/**
 * A feedback banner. Each tone pairs `{x}-muted` (background), `{x}-border`
 * and `{x}-text`, as the Figma variables intend (`feedback/{x}/bg`, `/border`,
 * `/fg` in the old token names).
 */
const alertVariants = cva(
  // An Ant Design icon is a span wrapping the svg, so it gets the same icon column as a bare svg.
  'relative w-full rounded-lg border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] has-[>.anticon]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 has-[>.anticon]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current [&>.anticon]:size-4 [&>.anticon]:translate-y-0.5 [&>.anticon]:text-current',
  {
    variants: {
      variant: {
        default: 'bg-card text-card-foreground',
        info: 'bg-info-muted border-info-border text-info-text',
        success: 'bg-success-muted border-success-border text-success-text',
        warning: 'bg-warning-muted border-warning-border text-warning-text',
        destructive: 'bg-destructive-muted border-destructive-border text-destructive-text',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function Alert({ className, variant, ...props }: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      data-variant={variant ?? 'default'}
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn('col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight', className)}
      {...props}
    />
  );
}

function AlertDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      // Block flow: inline text, <b> and links wrap naturally; paragraphs get a gap
      className={cn('col-start-2 space-y-1 text-sm [&_p]:leading-relaxed', className)}
      {...props}
    />
  );
}

export { Alert, AlertTitle, AlertDescription };
